import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AgGridReact } from 'ag-grid-react';
import type {
  CellClassParams,
  CellContextMenuEvent,
  CellDoubleClickedEvent,
  CellKeyDownEvent,
  CellStyle,
  CellValueChangedEvent,
  ColDef,
  ColGroupDef,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  RowClassParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import {
  ArrowRightToLine,
  ChevronDown,
  ChevronRight,
  Eraser,
  EyeOff,
  Link2,
  MessageSquare,
  MessageSquareText,
  PaintBucket,
  Pencil,
  Plus,
  Rows3,
  Trash2,
  X,
} from 'lucide-react';
import { atlasGridTheme } from '@/lib/agGrid';
import { useIsMobile } from '@/lib/useMediaQuery';
import { formatCurrency } from '@/lib/formatters';
import { formatMonthLabel } from '@/lib/months';
import {
  cashAlarm,
  checkAlarm,
  varianceAlarm,
  isSectionSummaryRow,
  sectionIsCollapsible,
  type AccountNode,
  type Alarm,
  type EngineRow,
  type Model,
  type SheetId,
  type WorkbookSettings,
} from '@/features/projections/engine';
import { buildTxDrilldownUrl } from '@/features/financials/transactionsDrilldown';
import { DEFAULT_DENSITY, ROW_HEIGHTS, type RowDensity } from '@/features/projections/density';
import type { PlanEdit } from '@/features/projections/useProjectionModel';
import type { CellStyleInput, CellStyleRow } from '@/features/projections/workbookHooks';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { Input } from '@/components/ui/Input';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type ColumnMode = 'split' | 'stacked';
export type ViewMode = 'amount' | 'pct-revenue';

export interface GridActions {
  insertLine: (opts: { afterKey: string; section: string; label: string }) => void;
  renameRow: (row: EngineRow, label: string) => void;
  deleteCustomRow: (rowId: string) => void;
  hideAccount: (accountId: string) => void;
  toggleRollup: (rowId: string) => void;
  setValueType: (rowId: string, valueType: 'currency' | 'units' | 'percent') => void;
}

export interface ProjectionsGridProps {
  sheet: SheetId;
  rows: EngineRow[];
  model: Model;
  visibleMonths: string[];
  settings: WorkbookSettings;
  styles: CellStyleRow[];
  zoom: number;
  columnMode: ColumnMode;
  density?: RowDensity;
  viewMode: ViewMode;
  height: number;
  onEdit: (edits: PlanEdit[]) => void;
  onSetStyle: (input: CellStyleInput) => void;
  actions?: GridActions;
  onGridReady?: (api: GridApi<EngineRow>) => void;
  /** Section header keys (`sec:*`) currently folded down to their total line. */
  collapsedSections?: Set<string>;
  onToggleSection?: (key: string) => void;
}

/** P&L groups whose actuals can be drilled into QuickBooks transactions. */
const DRILLABLE_GROUPS = new Set(['income', 'cogs', 'expense', 'other-income', 'other-expense']);

/**
 * Why a plan cell refuses typing. Shown as a toast when the operator tries
 * anyway, so a locked dash never looks like a broken cell.
 */
function lockedReason(row: EngineRow, col: 'plan' | 'actual' | null): string | null {
  if (col === 'actual') return "Actuals come straight from QuickBooks and can't be typed over.";
  if (row.editablePlan) return null;
  switch (row.planSource) {
    case 'sales':
      return `${row.label} is driven by the Sales projections — change customer units or pricing on the Sales drivers tab.`;
    case 'cogs':
      return `${row.label} is driven by unit COGS × projected units — adjust them on the Sales drivers tab.`;
    case 'capital':
      return 'This line comes from the Capital Planning tab.';
    default:
      break;
  }
  switch (row.kind) {
    case 'total':
    case 'calc':
    case 'ratio':
    case 'check':
      return 'This is a calculated line — edit the lines that feed into it.';
    case 'product':
      return 'Derived from the customer unit estimates above it.';
    case 'section':
    case 'spacer':
      return null;
    default:
      return "This line can't be edited here.";
  }
}

/** Inclusive YYYY-MM-DD bounds for one calendar month (`YYYY-MM-01` key). */
function monthBounds(month: string): { from: string; to: string } {
  const d = new Date(`${month}T00:00:00Z`);
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
  const to = `${last.getUTCFullYear()}-${String(last.getUTCMonth() + 1).padStart(2, '0')}-${String(
    last.getUTCDate(),
  ).padStart(2, '0')}`;
  return { from: month, to };
}

/** Highlight palette. Stored as the hex value so exports / AI stay simple. */
const HIGHLIGHT_COLORS: Array<{ name: string; value: string }> = [
  { name: 'Yellow', value: '#FEF3C7' },
  { name: 'Green', value: '#DCFCE7' },
  { name: 'Blue', value: '#DBEAFE' },
  { name: 'Purple', value: '#EDE9FE' },
  { name: 'Pink', value: '#FCE7F3' },
  { name: 'Orange', value: '#FFEDD5' },
  { name: 'Red', value: '#FEE2E2' },
  { name: 'Gray', value: '#E5E7EB' },
];

/** Sections that accept hand-inserted lines. */
const INSERTABLE_GROUPS = new Set([
  'sales',
  'income',
  'cogs',
  'expense',
  'other-income',
  'other-expense',
  'operating',
  'investing',
  'financing',
]);

const PLAN_PREFIX = 'p:';
const ACTUAL_PREFIX = 'a:';
const LABEL_COL = 'label';

const planCol = (m: string) => `${PLAN_PREFIX}${m}`;
const actualCol = (m: string) => `${ACTUAL_PREFIX}${m}`;

function parseCol(colId: string): { kind: 'plan' | 'actual'; month: string } | null {
  if (colId.startsWith(PLAN_PREFIX))
    return { kind: 'plan', month: colId.slice(PLAN_PREFIX.length) };
  if (colId.startsWith(ACTUAL_PREFIX))
    return { kind: 'actual', month: colId.slice(ACTUAL_PREFIX.length) };
  return null;
}

const styleKey = (rowKey: string, month: string | null, col: string | null) =>
  `${rowKey}|${month ?? ''}|${col ?? ''}`;

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** Engine value → display units (dollars / units / fraction). */
function toDisplay(row: EngineRow, v: number | null): number | null {
  if (v === null) return null;
  return row.valueType === 'currency' ? v / 100 : v;
}

function formatValue(row: EngineRow, v: number | null | undefined, viewMode: ViewMode): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '';
  if (viewMode === 'pct-revenue' && row.valueType === 'currency') {
    return `${(v * 100).toFixed(1)}%`;
  }
  if (row.valueType === 'percent') return v === 0 ? '' : `${(v * 100).toFixed(1)}%`;
  if (row.valueType === 'units') {
    if (v === 0) return '';
    return Number.isInteger(v)
      ? v.toLocaleString('en-US')
      : v.toLocaleString('en-US', { maximumFractionDigits: 1 });
  }
  if (v === 0) return '';
  return formatCurrency(Math.round(v * 100), { whole: true });
}

function alarmClass(a: Alarm): string {
  switch (a) {
    case 'bad':
      return 'pj-bad';
    case 'warn':
      return 'pj-warn';
    case 'good':
      return 'pj-good';
    default:
      return '';
  }
}

// ---------------------------------------------------------------------------
// Grid
// ---------------------------------------------------------------------------

interface MenuState {
  x: number;
  y: number;
  row: EngineRow;
  month: string | null;
  col: string | null;
  rowIndex: number;
}

interface UndoEntry {
  rowKey: string;
  month: string;
  prev: number;
  next: number;
}

export function ProjectionsGrid({
  sheet,
  rows,
  model,
  visibleMonths,
  settings,
  styles,
  zoom,
  columnMode,
  density = DEFAULT_DENSITY,
  viewMode,
  height,
  onEdit,
  onSetStyle,
  actions,
  onGridReady,
  collapsedSections,
  onToggleSection,
}: ProjectionsGridProps) {
  const apiRef = useRef<GridApi<EngineRow> | null>(null);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  // Where the Transactions page's "Back to Main Financials" button returns to.
  const backPath = `${location.pathname}${location.search}`;
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [noteTarget, setNoteTarget] = useState<{
    row: EngineRow;
    month: string | null;
    col: string | null;
    note: string;
  } | null>(null);
  const [renameTarget, setRenameTarget] = useState<{ row: EngineRow; label: string } | null>(null);
  const undoStack = useRef<UndoEntry[]>([]);
  const redoStack = useRef<UndoEntry[]>([]);

  const monthIndex = useMemo(() => {
    const m = new Map<string, number>();
    model.months.forEach((k, i) => m.set(k, i));
    return m;
  }, [model.months]);

  const rowsByKey = useMemo(() => {
    const m = new Map<string, EngineRow>();
    for (const r of rows) m.set(r.key, r);
    return m;
  }, [rows]);

  const styleMap = useMemo(() => {
    const m = new Map<string, CellStyleRow>();
    for (const s of styles) {
      if (s.sheet !== sheet) continue;
      m.set(styleKey(s.row_key, s.month, s.col), s);
    }
    return m;
  }, [styles, sheet]);

  const checkTolerance = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of model.checks) m.set(c.key, c.tolerance);
    return m;
  }, [model.checks]);

  // Collapse: hide rows that sit under a collapsed header until the next
  // row at the header's depth (its "Total …" row is hidden as well).
  const visibleRows = useMemo(() => {
    const sections = collapsedSections ?? new Set<string>();
    if (collapsed.size === 0 && sections.size === 0) return rows;
    const out: EngineRow[] = [];
    let hideBelowDepth: number | null = null;
    let hideTotalKey: string | null = null;
    let inFoldedSection = false;
    for (const r of rows) {
      // A folded section ends at the next section header / spacer and shows
      // only its summary lines in between.
      if (inFoldedSection) {
        if (r.kind === 'section' || r.kind === 'spacer') inFoldedSection = false;
        else if (!isSectionSummaryRow(r)) continue;
      }
      if (hideBelowDepth !== null) {
        if (r.depth > hideBelowDepth || r.key === hideTotalKey) continue;
        hideBelowDepth = null;
        hideTotalKey = null;
      }
      out.push(r);
      if (r.kind === 'section' && sections.has(r.key)) inFoldedSection = true;
      if (r.hasChildren && collapsed.has(r.key)) {
        hideBelowDepth = r.depth;
        hideTotalKey = r.accountId ? `tot:${r.accountId}` : `tot:${r.key.replace(/^hdr:/, '')}`;
      }
    }
    return out;
  }, [rows, collapsed, collapsedSections]);

  // Which rows can be folded (sections with real content underneath).
  const collapsibleSections = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows)
      if (r.kind === 'section' && sectionIsCollapsible(rows, r.key)) s.add(r.key);
    return s;
  }, [rows]);

  // ---- drill-down into QuickBooks transactions ------------------------------

  const pnlNodeById = useMemo(() => {
    const map = new Map<string, AccountNode>();
    const walk = (n: AccountNode) => {
      map.set(n.account.id, n);
      n.children.forEach(walk);
    };
    model.pnlRoots.forEach(walk);
    return map;
  }, [model.pnlRoots]);

  /** URL into the Transactions workspace for one row × month, or null. */
  const drillUrl = useCallback(
    (row: EngineRow | undefined, month: string): string | null => {
      if (!row || !DRILLABLE_GROUPS.has(row.group)) return null;
      const i = monthIndex.get(month);
      if (i === undefined || !model.monthHasActual[i]) return null;
      const actual = row.series.actual[i];
      if (actual === null || actual === 0) return null;

      const subtree = (n: AccountNode, into: string[] = []): string[] => {
        into.push(n.account.id);
        n.children.forEach((c) => subtree(c, into));
        return into;
      };
      let accountIds: string[] = [];
      if (row.key.startsWith('sectot:')) {
        for (const root of model.pnlRoots)
          if (root.section === row.group) subtree(root, accountIds);
      } else if (row.accountId) {
        const node = pnlNodeById.get(row.accountId);
        const wholeSubtree = row.kind === 'header' || row.key.startsWith('tot:');
        accountIds = node && wholeSubtree ? subtree(node) : [row.accountId];
      }
      if (accountIds.length === 0) return null;

      const { from, to } = monthBounds(month);
      return buildTxDrilldownUrl({
        accountIds,
        accountLabel: row.label,
        from,
        to,
        monthLabel: formatMonthLabel(month),
        source: 'Main Financials',
        back: backPath,
      });
    },
    [monthIndex, model.monthHasActual, model.pnlRoots, pnlNodeById, backPath],
  );

  const onDrill = useCallback(
    (url: string, e: { metaKey?: boolean; ctrlKey?: boolean }) => {
      if (e.metaKey || e.ctrlKey) window.open(url, '_blank', 'noopener');
      else navigate(url);
    },
    [navigate],
  );

  const toggleCollapse = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // ---- value access -------------------------------------------------------

  const revenueAt = useCallback(
    (i: number, kind: 'plan' | 'actual'): number | null =>
      kind === 'plan' ? model.revenue.plan[i] : model.revenue.actual[i],
    [model.revenue],
  );

  const cellValue = useCallback(
    (row: EngineRow, month: string, kind: 'plan' | 'actual'): number | null => {
      const i = monthIndex.get(month);
      if (i === undefined) return null;
      const raw = kind === 'plan' ? row.series.plan[i] : row.series.actual[i];
      if (viewMode === 'pct-revenue' && row.valueType === 'currency') {
        if (raw === null) return null;
        const rev = revenueAt(i, kind);
        if (!rev) return null;
        return raw / rev;
      }
      return toDisplay(row, raw);
    },
    [monthIndex, viewMode, revenueAt],
  );

  const isEditable = useCallback(
    (row: EngineRow | undefined, month: string): boolean => {
      if (!row || !row.editablePlan || viewMode !== 'amount') return false;
      return monthIndex.has(month);
    },
    [monthIndex, viewMode],
  );

  const pushEdit = useCallback(
    (edits: PlanEdit[], record = true) => {
      if (record) {
        for (const e of edits) {
          const i = monthIndex.get(e.month);
          if (i === undefined) continue;
          const prev = toDisplay(e.row, e.row.series.plan[i]) ?? 0;
          undoStack.current.push({ rowKey: e.row.key, month: e.month, prev, next: e.value });
        }
        redoStack.current = [];
        if (undoStack.current.length > 200) undoStack.current.splice(0, 50);
      }
      onEdit(edits);
    },
    [onEdit, monthIndex],
  );

  const undo = useCallback(() => {
    const e = undoStack.current.pop();
    if (!e) return;
    const row = rowsByKey.get(e.rowKey);
    if (!row) return;
    redoStack.current.push(e);
    pushEdit([{ row, month: e.month, value: e.prev }], false);
  }, [rowsByKey, pushEdit]);

  const redo = useCallback(() => {
    const e = redoStack.current.pop();
    if (!e) return;
    const row = rowsByKey.get(e.rowKey);
    if (!row) return;
    undoStack.current.push(e);
    pushEdit([{ row, month: e.month, value: e.next }], false);
  }, [rowsByKey, pushEdit]);

  // ---- column definitions -------------------------------------------------

  const monthCellClass = useCallback(
    (params: CellClassParams<EngineRow>, kind: 'plan' | 'actual', month: string): string[] => {
      const row = params.data;
      const classes: string[] = [kind === 'plan' ? 'pj-plan' : 'pj-actual'];
      if (!row) return classes;
      const i = monthIndex.get(month);
      if (i === undefined) return classes;
      const closed = model.monthIsClosed[i];
      if (closed) classes.push('pj-closed');
      if (kind === 'plan' && isEditable(row, month)) classes.push('pj-editable');
      if (row.kind === 'spacer' || row.kind === 'section') return classes;

      const plan = row.series.plan[i];
      const actual = row.series.actual[i];
      // Conditional formatting — centralised in the engine so the AI and the
      // grid flag the same cells.
      if (row.kind === 'check') {
        if (kind === 'actual' || columnMode === 'stacked')
          classes.push(alarmClass(checkAlarm(actual, checkTolerance.get(row.key) ?? 1)));
      } else if (row.key === 'calc:ending-cash' || row.key === 'calc:net-cash-after-cc') {
        const v = kind === 'plan' ? plan : actual;
        if (v !== null) classes.push(alarmClass(cashAlarm(v, settings)));
      } else if (row.key === 'ratio:gross-margin') {
        const v = kind === 'plan' ? plan : actual;
        if (v !== null && v !== 0 && v < settings.gross_margin_floor_pct) classes.push('pj-warn');
        if (kind === 'actual' && closed)
          classes.push(alarmClass(varianceAlarm(row, plan, actual, settings)));
      } else if ((kind === 'actual' || columnMode === 'stacked') && closed) {
        classes.push(alarmClass(varianceAlarm(row, plan, actual, settings)));
      }
      return classes;
    },
    [monthIndex, model.monthIsClosed, isEditable, columnMode, checkTolerance, settings],
  );

  const highlightStyle = useCallback(
    (row: EngineRow | undefined, month: string | null, col: string | null): CellStyle => {
      if (!row) return {};
      const s =
        styleMap.get(styleKey(row.key, month, col)) ??
        (month ? styleMap.get(styleKey(row.key, month, null)) : undefined) ??
        styleMap.get(styleKey(row.key, null, null));
      const style: CellStyle = { textAlign: col === LABEL_COL ? 'left' : 'right' };
      if (s?.color) style.backgroundColor = s.color;
      return style;
    },
    [styleMap],
  );

  // Highlights / notes live outside AG Grid's data model, so force a cell
  // refresh whenever they change to make sure cellStyle is re-evaluated.
  useEffect(() => {
    apiRef.current?.refreshCells({ force: true });
  }, [styleMap]);

  // getRowHeight is a callback, so the grid has to be told when its inputs
  // (density / zoom) change for section and spacer rows to re-measure.
  useEffect(() => {
    apiRef.current?.resetRowHeights();
  }, [density, zoom]);

  const noteFor = useCallback(
    (row: EngineRow | undefined, month: string | null, col: string | null): string | null => {
      if (!row) return null;
      return styleMap.get(styleKey(row.key, month, col))?.note ?? null;
    },
    [styleMap],
  );

  const columnDefs = useMemo<(ColDef<EngineRow> | ColGroupDef<EngineRow>)[]>(() => {
    const labelWidth = Math.round(250 * zoom);
    const monthWidth = Math.round((columnMode === 'stacked' ? 104 : 92) * zoom);
    const totalWidth = Math.round(108 * zoom);
    // Balance-sheet rows are point-in-time balances, so the right-hand
    // columns are end-of-range snapshots rather than sums (see engine summarise).
    const pointInTime = sheet === 'balance-sheet';

    const labelDef: ColDef<EngineRow> = {
      headerName: sheet === 'sales' ? 'Customer / product' : 'Line',
      colId: LABEL_COL,
      pinned: 'left',
      lockPinned: true,
      width: labelWidth,
      valueGetter: (p: ValueGetterParams<EngineRow>) => p.data?.label ?? '',
      cellRenderer: LabelCellRenderer,
      cellRendererParams: {
        collapsed,
        onToggle: toggleCollapse,
        noteFor: (row: EngineRow) => noteFor(row, null, LABEL_COL),
        zoom,
      },
      cellClass: ({ data }: CellClassParams<EngineRow>) => [
        'pj-label',
        `pj-kind-${data?.kind ?? ''}`,
      ],
      cellStyle: ({ data }: CellClassParams<EngineRow>) => highlightStyle(data, null, LABEL_COL),
      tooltipValueGetter: (p) => noteFor(p.data, null, LABEL_COL) ?? p.data?.hint ?? '',
    };

    const monthDefs: (ColDef<EngineRow> | ColGroupDef<EngineRow>)[] = visibleMonths.map((m) => {
      const i = monthIndex.get(m);
      const closed = i !== undefined && model.monthIsClosed[i];
      const hasActual = i !== undefined && model.monthHasActual[i];
      const isCurrent = m === model.currentMonth;
      const headerClass = [
        'atlas-center-header',
        closed ? 'pj-hdr-closed' : 'pj-hdr-open',
        isCurrent ? 'pj-hdr-current' : '',
      ].join(' ');

      const planDef: ColDef<EngineRow> = {
        headerName: columnMode === 'stacked' ? formatMonthLabel(m) : 'Plan',
        colId: planCol(m),
        width: monthWidth,
        headerClass,
        editable: (p) => isEditable(p.data, m),
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: { precision: 2 },
        valueGetter: (p: ValueGetterParams<EngineRow>) =>
          p.data ? cellValue(p.data, m, 'plan') : null,
        valueSetter: (p: ValueSetterParams<EngineRow>) => {
          if (!p.data || !isEditable(p.data, m)) return false;
          const idx = monthIndex.get(m);
          if (idx === undefined) return false;
          const next = p.newValue === null || p.newValue === '' ? 0 : Number(p.newValue);
          if (!Number.isFinite(next)) return false;
          const prev = toDisplay(p.data, p.data.series.plan[idx]) ?? 0;
          if (prev === next) return false;
          // Mutate the derived row so the grid shows the value instantly;
          // React Query's optimistic update replaces it a tick later.
          p.data.series.plan[idx] = p.data.valueType === 'currency' ? Math.round(next * 100) : next;
          return true;
        },
        valueFormatter: (p) =>
          p.data ? formatValue(p.data, p.value as number | null, viewMode) : '',
        cellClass: (p) => monthCellClass(p, 'plan', m),
        cellStyle: ({ data }: CellClassParams<EngineRow>) => highlightStyle(data, m, 'plan'),
        tooltipValueGetter: (p) => {
          const note = noteFor(p.data, m, 'plan') ?? noteFor(p.data, m, null);
          if (note) return note;
          if (!p.data || columnMode === 'split') return '';
          const a = cellValue(p.data, m, 'actual');
          return a === null ? '' : `Actual: ${formatValue(p.data, a, viewMode)}`;
        },
        ...(columnMode === 'stacked'
          ? {
              cellRenderer: StackedCellRenderer,
              cellRendererParams: {
                month: m,
                cellValue,
                viewMode,
                hasActual,
                noteFor,
                drillUrl,
                onDrill,
              },
            }
          : {}),
      };

      if (columnMode === 'stacked') return planDef;

      const actualDef: ColDef<EngineRow> = {
        headerName: 'Actual',
        colId: actualCol(m),
        width: monthWidth,
        headerClass,
        editable: false,
        valueGetter: (p: ValueGetterParams<EngineRow>) =>
          p.data ? cellValue(p.data, m, 'actual') : null,
        valueFormatter: (p) => {
          if (!p.data) return '';
          const v = p.value as number | null;
          if (
            (v === null || v === 0) &&
            hasActual &&
            p.data.kind !== 'spacer' &&
            p.data.kind !== 'section'
          )
            return p.data.kind === 'customer' || p.data.kind === 'product' ? '' : '—';
          return formatValue(p.data, v, viewMode);
        },
        cellClass: (p) => monthCellClass(p, 'actual', m),
        cellStyle: ({ data }: CellClassParams<EngineRow>) => highlightStyle(data, m, 'actual'),
        tooltipValueGetter: (p) => {
          const note = noteFor(p.data, m, 'actual') ?? noteFor(p.data, m, null);
          if (note) return note;
          return drillUrl(p.data, m) ? 'Click to see the QuickBooks transactions behind this' : '';
        },
        cellRenderer: NoteAwareCellRenderer,
        cellRendererParams: {
          noteFor: (row: EngineRow) => noteFor(row, m, 'actual'),
          drillUrl: (row: EngineRow) => drillUrl(row, m),
          onDrill,
        },
      };

      return {
        headerName: formatMonthLabel(m),
        groupId: `g:${m}`,
        headerClass,
        marryChildren: true,
        children: [planDef, actualDef],
      } satisfies ColGroupDef<EngineRow>;
    });

    const totalDef = (
      id: 'plan' | 'actual' | 'forecast' | 'variance',
      header: string,
    ): ColDef<EngineRow> => ({
      headerName: header,
      colId: `t:${id}`,
      pinned: 'right',
      lockPinned: true,
      width: totalWidth,
      headerClass: 'atlas-center-header pj-hdr-total',
      valueGetter: (p: ValueGetterParams<EngineRow>) => {
        const row = p.data;
        if (!row) return null;
        const v = row.totals[id];
        if (viewMode === 'pct-revenue' && row.valueType === 'currency') {
          const rev =
            id === 'actual' || id === 'variance'
              ? sumVisible(model.revenue.actual, visibleMonths, monthIndex)
              : id === 'forecast'
                ? forecastVisible(model.revenue, model, visibleMonths, monthIndex)
                : sumVisible(model.revenue.plan, visibleMonths, monthIndex);
          if (v === null || !rev) return null;
          return v / rev;
        }
        return toDisplay(row, v);
      },
      valueFormatter: (p) =>
        p.data ? formatValue(p.data, p.value as number | null, viewMode) : '',
      cellClass: (p) => {
        const row = p.data;
        const classes = ['pj-total', `pj-total-${id}`];
        if (!row || row.kind === 'spacer' || row.kind === 'section') return classes;
        if (
          id === 'variance' &&
          row.totals.variance !== null &&
          row.totals.actual !== null &&
          row.favorable !== 'none'
        ) {
          // Variance is Σ(actual − plan) over CLOSED months, so the plan to
          // compare against is the closed-month plan, not the full-range plan.
          const closedPlan = row.totals.actual - row.totals.variance;
          const a = varianceAlarm(row, closedPlan, row.totals.actual, settings);
          classes.push(alarmClass(a));
        }
        return classes;
      },
      cellStyle: ({ data }: CellClassParams<EngineRow>) => highlightStyle(data, null, `t:${id}`),
      tooltipValueGetter: (p) => {
        if (!p.data) return '';
        if (p.data.valueType === 'percent') return 'Percentages show the last visible month';
        if (pointInTime) {
          if (id === 'plan') return 'Planned balance at the end of the visible range';
          if (id === 'actual') return 'Latest closed-month actual balance in the visible range';
          if (id === 'forecast')
            return 'Balance at the end of the range (actual if closed, otherwise plan)';
          return 'Actual − plan balance for the latest closed month in range';
        }
        if (id === 'forecast') return 'Actuals for closed months + plan for open months';
        if (id === 'variance') return 'Actual − plan over closed months';
        return '';
      },
    });

    return [
      labelDef,
      ...monthDefs,
      totalDef('plan', pointInTime ? 'Plan (end)' : 'Total plan'),
      totalDef('actual', pointInTime ? 'Actual (latest)' : 'Total actual'),
      totalDef('forecast', pointInTime ? 'Forecast (end)' : 'Forecast'),
      totalDef('variance', pointInTime ? 'Variance (latest)' : 'Variance'),
    ];
  }, [
    sheet,
    zoom,
    columnMode,
    viewMode,
    visibleMonths,
    monthIndex,
    model,
    collapsed,
    toggleCollapse,
    noteFor,
    highlightStyle,
    isEditable,
    cellValue,
    monthCellClass,
    settings,
    drillUrl,
    onDrill,
  ]);

  const defaultColDef = useMemo<ColDef<EngineRow>>(
    () => ({
      sortable: false,
      filter: false,
      resizable: true,
      suppressMovable: true,
      menuTabs: [],
      suppressKeyboardEvent: (p) => {
        // Let the grid-level handler own Ctrl+Z / Ctrl+Y / Ctrl+V / Delete.
        const e = p.event;
        if (p.editing) return false;
        if ((e.ctrlKey || e.metaKey) && ['z', 'y', 'v', 'c'].includes(e.key.toLowerCase()))
          return true;
        return false;
      },
    }),
    [],
  );

  const baseRowHeight = ROW_HEIGHTS[density][columnMode];
  const theme = useMemo(
    () =>
      atlasGridTheme.withParams({
        fontSize: Math.round(13 * zoom * 10) / 10,
        headerFontSize: Math.round(11 * zoom * 10) / 10,
        rowHeight: Math.round(baseRowHeight * zoom),
        headerHeight: Math.round(28 * zoom),
        cellHorizontalPadding: Math.round(8 * zoom),
        spacing: Math.max(2, Math.round(5 * zoom)),
      }),
    [zoom, baseRowHeight],
  );

  // ---- events ---------------------------------------------------------

  const handleGridReady = (p: GridReadyEvent<EngineRow>) => {
    apiRef.current = p.api;
    onGridReady?.(p.api);
  };

  const onCellValueChanged = (e: CellValueChangedEvent<EngineRow>) => {
    const row = e.data;
    const col = parseCol(e.column.getColId());
    if (!row || !col || col.kind !== 'plan') return;
    const value = e.newValue === null || e.newValue === '' ? 0 : Number(e.newValue);
    if (!Number.isFinite(value)) return;
    const prev = e.oldValue === null || e.oldValue === undefined ? 0 : Number(e.oldValue);
    undoStack.current.push({ rowKey: row.key, month: col.month, prev, next: value });
    redoStack.current = [];
    onEdit([{ row, month: col.month, value }]);
  };

  const focusedCellInfo = useCallback((): {
    row: EngineRow;
    month: string | null;
    col: string | null;
    rowIndex: number;
  } | null => {
    const api = apiRef.current;
    const fc = api?.getFocusedCell();
    if (!api || !fc) return null;
    const node = api.getDisplayedRowAtIndex(fc.rowIndex);
    const row = node?.data;
    if (!row) return null;
    const colId = fc.column.getColId();
    const parsed = parseCol(colId);
    return {
      row,
      month: parsed?.month ?? null,
      col: parsed ? parsed.kind : colId,
      rowIndex: fc.rowIndex,
    };
  }, []);

  const pasteFromClipboard = useCallback(async () => {
    const start = focusedCellInfo();
    if (!start || !start.month || start.col !== 'plan') return;
    let text = '';
    try {
      text = await navigator.clipboard.readText();
    } catch {
      return;
    }
    if (!text) return;
    const lines = text
      .replace(/\r/g, '')
      .split('\n')
      .filter((l, i, arr) => !(i === arr.length - 1 && l === ''));
    const startMonthIdx = visibleMonths.indexOf(start.month);
    if (startMonthIdx < 0) return;
    const edits: PlanEdit[] = [];
    let rowIdx = start.rowIndex;
    for (const line of lines) {
      const node = apiRef.current?.getDisplayedRowAtIndex(rowIdx);
      const row = node?.data;
      rowIdx += 1;
      if (!row) break;
      const cells = line.split('\t');
      cells.forEach((raw, j) => {
        const m = visibleMonths[startMonthIdx + j];
        if (!m || !isEditable(row, m)) return;
        const cleaned = raw.replace(/[$,%\s]/g, '').replace(/^\((.*)\)$/, '-$1');
        if (cleaned === '') return;
        const v = Number(cleaned);
        if (!Number.isFinite(v)) return;
        edits.push({ row, month: m, value: row.valueType === 'percent' ? v / 100 : v });
      });
    }
    if (edits.length) pushEdit(edits);
  }, [focusedCellInfo, visibleMonths, isEditable, pushEdit]);

  const copyFocused = useCallback(async () => {
    const info = focusedCellInfo();
    if (!info) return;
    let text = '';
    if (info.month && (info.col === 'plan' || info.col === 'actual')) {
      const v = cellValue(info.row, info.month, info.col);
      text = v === null ? '' : String(v);
    } else {
      text = info.row.label;
    }
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard may be blocked — nothing to do.
    }
  }, [focusedCellInfo, cellValue]);

  const onCellKeyDown = (e: CellKeyDownEvent<EngineRow>) => {
    const ev = e.event as KeyboardEvent | undefined;
    if (!ev) return;
    const key = ev.key.toLowerCase();
    const mod = ev.ctrlKey || ev.metaKey;
    if (mod && key === 'z' && !ev.shiftKey) {
      ev.preventDefault();
      undo();
    } else if ((mod && key === 'y') || (mod && key === 'z' && ev.shiftKey)) {
      ev.preventDefault();
      redo();
    } else if (mod && key === 'v') {
      ev.preventDefault();
      void pasteFromClipboard();
    } else if (mod && key === 'c') {
      ev.preventDefault();
      void copyFocused();
    } else if ((ev.key === 'Delete' || ev.key === 'Backspace') && !mod) {
      const info = focusedCellInfo();
      if (info?.month && info.col === 'plan' && isEditable(info.row, info.month)) {
        ev.preventDefault();
        pushEdit([{ row: info.row, month: info.month, value: 0 }]);
      }
    } else if (!mod && (ev.key.length === 1 || ev.key === 'F2')) {
      const info = focusedCellInfo();
      if (info?.month && !isEditable(info.row, info.month)) explainLocked(info.row, info.col);
    }
  };

  const explainLocked = (row: EngineRow, col: string | null) => {
    if (viewMode !== 'amount') {
      toast.info('Switch the view back to Dollars to edit projections.');
      return;
    }
    const reason = lockedReason(row, col === 'plan' || col === 'actual' ? col : null);
    if (reason) toast.info(reason, { id: `locked:${row.key}` });
  };

  const onCellDoubleClicked = (e: CellDoubleClickedEvent<EngineRow>) => {
    const row = e.data;
    const col = parseCol(e.column.getColId());
    if (!row || !col) return;
    if (!isEditable(row, col.month)) explainLocked(row, col.kind);
  };

  const onCellContextMenu = (e: CellContextMenuEvent<EngineRow>) => {
    const ev = e.event as MouseEvent | undefined;
    const row = e.data;
    if (!ev || !row || row.kind === 'spacer') return;
    ev.preventDefault();
    const colId = e.column.getColId();
    const parsed = parseCol(colId);
    setMenu({
      x: ev.clientX,
      y: ev.clientY,
      row,
      month: parsed?.month ?? null,
      col: parsed ? parsed.kind : colId,
      rowIndex: e.rowIndex ?? 0,
    });
  };

  // Close the menu on outside click / escape / scroll.
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('mousedown', close);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', close);
    window.addEventListener('wheel', close, { passive: true });
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', close);
      window.removeEventListener('wheel', close);
    };
  }, [menu]);

  // ---- context-menu actions ----------------------------------------------

  const fillRight = (row: EngineRow, month: string) => {
    const start = visibleMonths.indexOf(month);
    if (start < 0) return;
    const v = cellValue(row, month, 'plan') ?? 0;
    const edits: PlanEdit[] = [];
    for (const m of visibleMonths.slice(start + 1)) {
      if (isEditable(row, m)) edits.push({ row, month: m, value: v });
    }
    if (edits.length) pushEdit(edits);
  };

  const clearRow = (row: EngineRow) => {
    const edits: PlanEdit[] = [];
    for (const m of visibleMonths) {
      if (isEditable(row, m) && (cellValue(row, m, 'plan') ?? 0) !== 0)
        edits.push({ row, month: m, value: 0 });
    }
    if (edits.length) pushEdit(edits);
  };

  const growRow = (row: EngineRow, month: string, pct: number) => {
    const start = visibleMonths.indexOf(month);
    if (start < 0) return;
    let v = cellValue(row, month, 'plan') ?? 0;
    const edits: PlanEdit[] = [];
    for (const m of visibleMonths.slice(start + 1)) {
      v = v * (1 + pct);
      if (isEditable(row, m)) {
        const rounded = row.valueType === 'units' ? Math.round(v) : Math.round(v * 100) / 100;
        edits.push({ row, month: m, value: rounded });
      }
    }
    if (edits.length) pushEdit(edits);
  };

  const setHighlight = (target: MenuState, color: string | null, scope: 'cell' | 'row') => {
    onSetStyle({
      sheet,
      row_key: target.row.key,
      month: scope === 'cell' ? target.month : null,
      col: scope === 'cell' ? target.col : null,
      color,
    });
  };

  const openNote = (target: MenuState) => {
    const existing = noteFor(target.row, target.month, target.col) ?? '';
    setNoteTarget({ row: target.row, month: target.month, col: target.col, note: existing });
  };

  const insertRelative = (target: MenuState, where: 'above' | 'below') => {
    if (!actions) return;
    const row = target.row;
    const section = row.group;
    let afterKey = row.key;
    if (where === 'above') {
      const idx = visibleRows.findIndex((r) => r.key === row.key);
      const prev = idx > 0 ? visibleRows[idx - 1] : null;
      if (prev && (prev.kind === 'account' || prev.kind === 'custom' || prev.kind === 'customer'))
        afterKey = prev.key;
      else if (prev && prev.kind === 'section') afterKey = row.key;
    }
    if (row.kind === 'header') afterKey = row.key;
    if (row.kind === 'total' && row.accountId) afterKey = `hdr:${row.accountId}`;
    actions.insertLine({ afterKey, section, label: 'New line' });
  };

  const canInsert = (row: EngineRow) =>
    !!actions &&
    INSERTABLE_GROUPS.has(row.group) &&
    row.kind !== 'section' &&
    row.kind !== 'spacer';

  // ---- render ---------------------------------------------------------

  if (isMobile) {
    return <MobileSummary rows={rows} model={model} visibleMonths={visibleMonths} />;
  }

  return (
    <div className="relative">
      <div
        className={`atlas-projections-grid ${columnMode === 'stacked' ? 'pj-stacked' : ''}`}
        style={{ height, width: '100%' }}
        onContextMenu={(e) => e.preventDefault()}
      >
        <AgGridReact<EngineRow>
          theme={theme}
          rowData={visibleRows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={(p) => p.data.key}
          isFullWidthRow={(p) => p.rowNode.data?.kind === 'section'}
          fullWidthCellRenderer={SectionRenderer}
          fullWidthCellRendererParams={{
            collapsedSections: collapsedSections ?? new Set<string>(),
            collapsibleSections,
            onToggle: onToggleSection,
          }}
          getRowClass={getRowClass}
          getRowHeight={(p) => {
            const k = p.data?.kind;
            if (k === 'spacer')
              return Math.max(6, Math.round((density === 'compact' ? 8 : 12) * zoom));
            if (k === 'section')
              return Math.round(
                (density === 'compact' ? 24 : density === 'normal' ? 27 : 30) * zoom,
              );
            return undefined;
          }}
          onCellValueChanged={onCellValueChanged}
          onCellKeyDown={onCellKeyDown}
          onCellDoubleClicked={onCellDoubleClicked}
          onCellContextMenu={onCellContextMenu}
          onGridReady={handleGridReady}
          stopEditingWhenCellsLoseFocus
          enterNavigatesVertically
          enterNavigatesVerticallyAfterEdit
          animateRows={false}
          suppressScrollOnNewData
          tooltipShowDelay={400}
          groupHeaderHeight={Math.round(26 * zoom)}
        />
      </div>

      {menu && (
        <ContextMenu
          menu={menu}
          onClose={() => setMenu(null)}
          canEdit={!!menu.month && menu.col === 'plan' && isEditable(menu.row, menu.month)}
          canInsert={canInsert(menu.row)}
          hasActions={!!actions}
          onHighlight={(c, scope) => setHighlight(menu, c, scope)}
          onNote={() => openNote(menu)}
          onFillRight={() => menu.month && fillRight(menu.row, menu.month)}
          onGrow={(pct) => menu.month && growRow(menu.row, menu.month, pct)}
          onClearRow={() => clearRow(menu.row)}
          onInsert={(where) => insertRelative(menu, where)}
          onRename={() => setRenameTarget({ row: menu.row, label: menu.row.label })}
          onDelete={() => menu.row.rowId && actions?.deleteCustomRow(menu.row.rowId)}
          onHide={() => menu.row.accountId && actions?.hideAccount(menu.row.accountId)}
          onToggleRollup={() => menu.row.rowId && actions?.toggleRollup(menu.row.rowId)}
          onSetValueType={(vt) => menu.row.rowId && actions?.setValueType(menu.row.rowId, vt)}
          onCopy={() => void copyFocused()}
        />
      )}

      <Dialog open={!!noteTarget} onOpenChange={(o) => !o && setNoteTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cell note</DialogTitle>
            <DialogDescription>
              {noteTarget
                ? `${noteTarget.row.label}${noteTarget.month ? ` · ${formatMonthLabel(noteTarget.month)}` : ''}${
                    noteTarget.col && noteTarget.col !== LABEL_COL ? ` · ${noteTarget.col}` : ''
                  }`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={noteTarget?.note ?? ''}
            onChange={(e) => noteTarget && setNoteTarget({ ...noteTarget, note: e.target.value })}
            rows={4}
            placeholder="Why this number? Assumptions, sources, follow-ups…"
            autoFocus
          />
          <DialogFooter className="gap-2">
            {noteTarget && noteFor(noteTarget.row, noteTarget.month, noteTarget.col) && (
              <Button
                variant="ghost"
                onClick={() => {
                  onSetStyle({
                    sheet,
                    row_key: noteTarget.row.key,
                    month: noteTarget.month,
                    col: noteTarget.col,
                    note: null,
                  });
                  setNoteTarget(null);
                }}
              >
                Remove note
              </Button>
            )}
            <Button variant="outline" onClick={() => setNoteTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!noteTarget) return;
                onSetStyle({
                  sheet,
                  row_key: noteTarget.row.key,
                  month: noteTarget.month,
                  col: noteTarget.col,
                  note: noteTarget.note.trim() || null,
                });
                setNoteTarget(null);
              }}
            >
              Save note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename line</DialogTitle>
            <DialogDescription>
              {renameTarget?.row.kind === 'account'
                ? 'Only the label in this workbook changes — QuickBooks is untouched.'
                : 'Give this line a clear name for the exec team.'}
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!renameTarget || !actions) return;
              const label = renameTarget.label.trim();
              if (label) actions.renameRow(renameTarget.row, label);
              setRenameTarget(null);
            }}
            className="space-y-4"
          >
            <Input
              value={renameTarget?.label ?? ''}
              onChange={(e) =>
                renameTarget && setRenameTarget({ ...renameTarget, label: e.target.value })
              }
              autoFocus
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenameTarget(null)}>
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Totals helpers for % of revenue view
// ---------------------------------------------------------------------------

function sumVisible(
  series: (number | null)[],
  visibleMonths: string[],
  monthIndex: Map<string, number>,
): number {
  let s = 0;
  for (const m of visibleMonths) {
    const i = monthIndex.get(m);
    if (i === undefined) continue;
    s += series[i] ?? 0;
  }
  return s;
}

function forecastVisible(
  series: { plan: number[]; actual: (number | null)[] },
  model: Model,
  visibleMonths: string[],
  monthIndex: Map<string, number>,
): number {
  let s = 0;
  for (const m of visibleMonths) {
    const i = monthIndex.get(m);
    if (i === undefined) continue;
    const a = series.actual[i];
    s += model.monthIsClosed[i] && a !== null ? a : series.plan[i];
  }
  return s;
}

// ---------------------------------------------------------------------------
// Renderers
// ---------------------------------------------------------------------------

function getRowClass(params: RowClassParams<EngineRow>): string | undefined {
  const r = params.data;
  if (!r) return undefined;
  const classes = [`pj-row-${r.kind}`];
  if (r.kind === 'total' && r.depth === 0) classes.push('pj-row-grand');
  if (r.key === 'calc:ending-cash' || r.key === 'calc:net-income' || r.key === 'calc:gross-profit')
    classes.push('pj-row-key');
  return classes.join(' ');
}

function SectionRenderer(
  params: ICellRendererParams<EngineRow> & {
    collapsedSections: Set<string>;
    collapsibleSections: Set<string>;
    onToggle?: (key: string) => void;
  },
) {
  const row = params.data;
  if (!row) return null;
  const collapsible = !!params.onToggle && params.collapsibleSections.has(row.key);
  const isCollapsed = params.collapsedSections.has(row.key);
  const Tag = collapsible ? 'button' : 'div';
  return (
    <Tag
      type={collapsible ? 'button' : undefined}
      className={`pj-section flex h-full w-full items-center gap-1.5 px-2 text-left text-xs font-semibold uppercase tracking-wide ${
        collapsible ? 'cursor-pointer hover:brightness-95' : ''
      }`}
      onClick={collapsible ? () => params.onToggle?.(row.key) : undefined}
      aria-expanded={collapsible ? !isCollapsed : undefined}
      title={collapsible ? (isCollapsed ? 'Expand section' : 'Collapse section') : undefined}
    >
      {collapsible ? (
        isCollapsed ? (
          <ChevronRight className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 shrink-0" />
        )
      ) : (
        <span className="w-3.5 shrink-0" />
      )}
      <span className="truncate">{row.label}</span>
      {isCollapsed && (
        <span className="ml-1 normal-case tracking-normal text-muted-foreground">
          · totals only
        </span>
      )}
    </Tag>
  );
}

/** Actual-value link into the Transactions workspace (Cmd/Ctrl-click → new tab). */
function DrillLink({
  url,
  onDrill,
  className,
  children,
}: {
  url: string;
  onDrill: (url: string, e: { metaKey?: boolean; ctrlKey?: boolean }) => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <a
      href={url}
      className={`pj-drill ${className ?? ''}`}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey) return;
        e.preventDefault();
        e.stopPropagation();
        onDrill(url, e);
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {children}
    </a>
  );
}

function LabelCellRenderer(
  params: ICellRendererParams<EngineRow> & {
    collapsed: Set<string>;
    onToggle: (key: string) => void;
    noteFor: (row: EngineRow) => string | null;
    zoom: number;
  },
) {
  const row = params.data;
  if (!row) return null;
  const indent = row.depth * 14;
  const note = params.noteFor(row);
  const isCollapsed = params.collapsed.has(row.key);
  return (
    <div className="flex h-full items-center gap-1 overflow-hidden" style={{ paddingLeft: indent }}>
      {row.hasChildren ? (
        <button
          type="button"
          className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-secondary hover:text-foreground"
          onClick={(e) => {
            e.stopPropagation();
            params.onToggle(row.key);
          }}
          aria-label={isCollapsed ? 'Expand' : 'Collapse'}
        >
          {isCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
      ) : (
        <span className="w-4 shrink-0" />
      )}
      <span className="min-w-0 flex-1 truncate">
        <span className={row.kind === 'total' || row.kind === 'calc' ? 'font-medium' : ''}>
          {row.label}
        </span>
        {row.hint && row.kind !== 'check' && (
          <span className="ml-1.5 truncate text-[0.8em] text-muted-foreground/70">{row.hint}</span>
        )}
      </span>
      {row.planSource === 'sales' || row.planSource === 'cogs' ? (
        <Link2 className="h-3 w-3 shrink-0 text-muted-foreground/60" aria-label="Driven by sales" />
      ) : null}
      {note && (
        <MessageSquareText className="h-3 w-3 shrink-0 text-amber-600" aria-label="Has a note" />
      )}
    </div>
  );
}

function NoteAwareCellRenderer(
  params: ICellRendererParams<EngineRow> & {
    noteFor: (row: EngineRow) => string | null;
    drillUrl?: (row: EngineRow) => string | null;
    onDrill?: (url: string, e: { metaKey?: boolean; ctrlKey?: boolean }) => void;
  },
) {
  const row = params.data;
  const note = row ? params.noteFor(row) : null;
  const url = row && params.drillUrl && params.onDrill ? params.drillUrl(row) : null;
  const text = params.valueFormatted ?? '';
  return (
    <span className="relative block h-full w-full">
      {url && params.onDrill ? (
        <DrillLink url={url} onDrill={params.onDrill}>
          {text}
        </DrillLink>
      ) : (
        text
      )}
      {note && <span className="pj-note-dot" aria-hidden />}
    </span>
  );
}

/** Rows that never carry a QuickBooks actual (sales drivers, checks, blanks). */
function rowHasNoActualLine(row: EngineRow): boolean {
  return row.kind === 'customer' || row.kind === 'product' || row.kind === 'check';
}

function StackedCellRenderer(
  params: ICellRendererParams<EngineRow> & {
    month: string;
    cellValue: (row: EngineRow, month: string, kind: 'plan' | 'actual') => number | null;
    viewMode: ViewMode;
    hasActual: boolean;
    noteFor: (
      row: EngineRow | undefined,
      month: string | null,
      col: string | null,
    ) => string | null;
    drillUrl: (row: EngineRow | undefined, month: string) => string | null;
    onDrill: (url: string, e: { metaKey?: boolean; ctrlKey?: boolean }) => void;
  },
) {
  const row = params.data;
  if (!row || row.kind === 'spacer') return null;
  const plan = params.cellValue(row, params.month, 'plan');
  const actual = params.cellValue(row, params.month, 'actual');
  const note = params.noteFor(row, params.month, 'plan') ?? params.noteFor(row, params.month, null);
  const isCheck = row.kind === 'check';

  // Both lines are always present so the eye can rely on "plan on top,
  // actual underneath". A blank plan shows as a soft dash instead of
  // disappearing; the actual line shows a dash for any month QuickBooks has
  // data for, and stays empty for months that have not happened yet.
  const planText = formatValue(row, plan, params.viewMode);
  const actualText = formatValue(row, actual, params.viewMode);
  const actualPending = params.hasActual && !rowHasNoActualLine(row);
  const url = params.drillUrl(row, params.month);

  return (
    <div className="pj-stack relative flex h-full flex-col justify-center leading-none">
      {!isCheck && (
        <span className={`pj-stack-plan ${planText ? '' : 'pj-blank'}`}>{planText || '—'}</span>
      )}
      <span className={`pj-stack-actual ${actualText ? '' : 'pj-blank'}`}>
        {url ? (
          <DrillLink url={url} onDrill={params.onDrill}>
            {actualText}
          </DrillLink>
        ) : (
          actualText || (actualPending ? '—' : '')
        )}
      </span>
      {note && <span className="pj-note-dot" aria-hidden />}
    </div>
  );
}

/**
 * Phone fallback (per `.cursor/rules/mobile-ui.mdc`): the spreadsheet is
 * illegible below `md`, so show the headline lines as cards instead.
 */
function MobileSummary({
  rows,
  model,
  visibleMonths,
}: {
  rows: EngineRow[];
  model: Model;
  visibleMonths: string[];
}) {
  const keyRows = rows.filter(
    (r) => (r.kind === 'total' && r.depth === 0) || r.kind === 'calc' || r.kind === 'ratio',
  );
  const first = visibleMonths[0];
  const last = visibleMonths[visibleMonths.length - 1];
  const curIdx = model.months.indexOf(model.currentMonth);
  return (
    <div className="space-y-3">
      <p className="rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
        Open on a larger screen for the full projections grid. Showing totals for{' '}
        {first ? formatMonthLabel(first) : ''}–{last ? formatMonthLabel(last) : ''}.
      </p>
      <ul className="space-y-2">
        {keyRows.map((r) => (
          <li key={r.key} className="rounded-lg border border-border bg-card px-3 py-2">
            <p className="text-sm font-medium">{r.label}</p>
            <div className="mt-1 grid grid-cols-3 gap-2 text-xs">
              <div>
                <p className="text-muted-foreground">Forecast</p>
                <p className="tabular-nums">
                  {formatValue(r, toDisplay(r, r.totals.forecast), 'amount') || '—'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Plan</p>
                <p className="tabular-nums">
                  {formatValue(r, toDisplay(r, r.totals.plan), 'amount') || '—'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">This month plan</p>
                <p className="tabular-nums">
                  {curIdx >= 0
                    ? formatValue(r, toDisplay(r, r.series.plan[curIdx]), 'amount') || '—'
                    : '—'}
                </p>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Context menu
// ---------------------------------------------------------------------------

function ContextMenu({
  menu,
  onClose,
  canEdit,
  canInsert,
  hasActions,
  onHighlight,
  onNote,
  onFillRight,
  onGrow,
  onClearRow,
  onInsert,
  onRename,
  onDelete,
  onHide,
  onToggleRollup,
  onSetValueType,
  onCopy,
}: {
  menu: MenuState;
  onClose: () => void;
  canEdit: boolean;
  canInsert: boolean;
  hasActions: boolean;
  onHighlight: (color: string | null, scope: 'cell' | 'row') => void;
  onNote: () => void;
  onFillRight: () => void;
  onGrow: (pct: number) => void;
  onClearRow: () => void;
  onInsert: (where: 'above' | 'below') => void;
  onRename: () => void;
  onDelete: () => void;
  onHide: () => void;
  onToggleRollup: () => void;
  onSetValueType: (vt: 'currency' | 'units' | 'percent') => void;
  onCopy: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState({ x: menu.x, y: menu.y });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = Math.min(menu.x, window.innerWidth - r.width - 8);
    const y = Math.min(menu.y, window.innerHeight - r.height - 8);
    setPos({ x: Math.max(8, x), y: Math.max(8, y) });
  }, [menu.x, menu.y]);

  const row = menu.row;
  const isCustom = row.kind === 'custom' && !!row.rowId;
  const isAccount = row.kind === 'account' && !!row.accountId;
  const canRename = hasActions && (isCustom || isAccount || row.kind === 'header');
  const item = (
    label: string,
    icon: ReactNode,
    onClick: () => void,
    opts?: { danger?: boolean; disabled?: boolean },
  ) => (
    <button
      type="button"
      disabled={opts?.disabled}
      className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-secondary disabled:opacity-40 ${
        opts?.danger ? 'text-destructive' : ''
      }`}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={() => {
        onClick();
        onClose();
      }}
    >
      <span className="text-muted-foreground">{icon}</span>
      {label}
    </button>
  );

  return (
    <div
      ref={ref}
      className="fixed z-50 w-64 rounded-md border border-border bg-card p-1.5 shadow-lg"
      style={{ left: pos.x, top: pos.y }}
      onMouseDown={(e) => e.stopPropagation()}
      role="menu"
    >
      <div className="truncate px-2 pb-1.5 pt-1 text-xs font-medium text-muted-foreground">
        {row.label}
        {menu.month ? ` · ${formatMonthLabel(menu.month)}` : ''}
      </div>
      <div className="px-2 pb-2">
        <div className="mb-1 flex items-center gap-1 text-[11px] text-muted-foreground">
          <PaintBucket className="h-3 w-3" /> Highlight {menu.month ? 'cell' : 'row'}
        </div>
        <div className="flex flex-wrap gap-1">
          {HIGHLIGHT_COLORS.map((c) => (
            <button
              key={c.value}
              type="button"
              title={c.name}
              className="h-6 w-6 rounded border border-border/70 hover:ring-2 hover:ring-primary/40"
              style={{ backgroundColor: c.value }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => {
                onHighlight(c.value, menu.month ? 'cell' : 'row');
                onClose();
              }}
            />
          ))}
          <button
            type="button"
            title="Clear highlight"
            className="flex h-6 w-6 items-center justify-center rounded border border-dashed border-border text-muted-foreground hover:bg-secondary"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => {
              onHighlight(null, menu.month ? 'cell' : 'row');
              onClose();
            }}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
        {menu.month && (
          <button
            type="button"
            className="mt-1.5 text-[11px] text-muted-foreground underline-offset-2 hover:underline"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => {
              onHighlight(HIGHLIGHT_COLORS[0].value, 'row');
              onClose();
            }}
          >
            Highlight whole row instead
          </button>
        )}
      </div>
      <div className="my-1 h-px bg-border" />
      {item('Add / edit note…', <MessageSquare className="h-3.5 w-3.5" />, onNote)}
      {item('Copy value', <Rows3 className="h-3.5 w-3.5" />, onCopy)}
      {canEdit && (
        <>
          <div className="my-1 h-px bg-border" />
          {item(
            'Fill right (copy to later months)',
            <ArrowRightToLine className="h-3.5 w-3.5" />,
            onFillRight,
          )}
          {item('Grow 2% / month from here', <ArrowRightToLine className="h-3.5 w-3.5" />, () =>
            onGrow(0.02),
          )}
          {item('Grow 5% / month from here', <ArrowRightToLine className="h-3.5 w-3.5" />, () =>
            onGrow(0.05),
          )}
          {item('Clear plan on this row', <Eraser className="h-3.5 w-3.5" />, onClearRow)}
        </>
      )}
      {(canInsert || canRename || isCustom || isAccount) && <div className="my-1 h-px bg-border" />}
      {canInsert &&
        item('Insert line below', <Plus className="h-3.5 w-3.5" />, () => onInsert('below'))}
      {canInsert &&
        row.kind !== 'header' &&
        row.kind !== 'total' &&
        item('Insert line above', <Plus className="h-3.5 w-3.5" />, () => onInsert('above'))}
      {canRename && item('Rename…', <Pencil className="h-3.5 w-3.5" />, onRename)}
      {isCustom && hasActions && (
        <>
          {item(
            row.hint?.startsWith('Memo')
              ? 'Include in totals'
              : 'Make memo line (exclude from totals)',
            <Rows3 className="h-3.5 w-3.5" />,
            onToggleRollup,
          )}
          <div className="flex gap-1 px-2 py-1">
            {(['currency', 'units', 'percent'] as const).map((vt) => (
              <button
                key={vt}
                type="button"
                className={`rounded border px-1.5 py-0.5 text-[11px] ${
                  row.valueType === vt
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:bg-secondary'
                }`}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => {
                  onSetValueType(vt);
                  onClose();
                }}
              >
                {vt === 'currency' ? '$' : vt === 'units' ? '#' : '%'}
              </button>
            ))}
          </div>
          {item('Delete line', <Trash2 className="h-3.5 w-3.5" />, onDelete, { danger: true })}
        </>
      )}
      {isAccount &&
        hasActions &&
        row.planSource !== 'sales' &&
        row.planSource !== 'cogs' &&
        item('Hide this account', <EyeOff className="h-3.5 w-3.5" />, onHide)}
    </div>
  );
}
